import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import { temporaryEndpoints } from 'http/endpoints';
import { LoadingState, LoadingStates } from 'globals/types';
import { LargeToggleTabButtonDataProps } from 'components/UserPanel/Pages/Dashboard/DashboardToggleChart/LargeToggleTabButton/LargeToggleTabButton';
import {
  DashboardSingleChart,
  DashboardSingleChartProps,
} from './dashboard-single-chart/dashboard-single-chart';
import { SingleBarProps } from './dashboard-single-chart/single-bar/single-bar';

export interface DashboardChartsProps {
  data: DashboardSingleChartProps[];
  status: LoadingState;
}

export const DashboardCharts = types
  .model({
    data: types.optional(types.array(DashboardSingleChart), []),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: DashboardChartsProps[]): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
  }))
  .actions((self) => ({
    async setDashboardCharts(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.dashboard.charts,
      });
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setDashboardCharts();
    },
    async update(): Promise<void> {
      await self.setDashboardCharts();
    },
  }))
  .views((self) => ({
    getTabs(): LargeToggleTabButtonDataProps[] {
      const { data } = self;
      const chartsSnapshot = getSnapshot(data);
      return chartsSnapshot.map(({ name, tab }, idx) => ({
        ...tab,
        id: idx,
        name,
        effectiveness: {
          percentageValue: Math.abs(tab.effectiveness.percentageValue),
          growth: tab.effectiveness.percentageValue > 0,
        },
      }));
    },
    getChart(idx: number): SingleBarProps[] {
      const { data } = self;
      return getSnapshot(data).map(({ chart }) => [...chart])[idx] || [];
    },
  }));

export const dashboardChars = DashboardCharts.create();
export type DashboardChartsSnapshot = SnapshotIn<typeof DashboardCharts>;
export type DashboardChartsInstance = Instance<typeof DashboardCharts>;
export default dashboardChars;
